import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import type {
  FilterConfig,
  FilterState,
  QuickFilterLayerFor,
  UseFiltersResult,
} from "#app/lib/filters/types";
import { ASSIGNEE_PRINCIPAL_FIELD } from "#app/lib/filters/build-where";
import { hasQuickFilterValue } from "#app/lib/filters/quick-values";
import { useFilters } from "#app/lib/hooks/use-filters";
import { useIssueFilterConfig } from "#app/lib/hooks/use-issue-filter-config";
import { isCustomFieldFilterField } from "#app/lib/filters/custom-field-filter-fields";
import { migrateStoredFilterState } from "#app/lib/filters/stored-filters";
import { combineWhereConditions } from "#app/lib/query-utils";
import type { ViewConfig } from "#app/lib/view-config";
import { useViewsOptional } from "#app/lib/views/view-context";
import {
  useAnalytics,
  AnalyticsContext,
  IssueEvent,
  type AnalyticsScope,
} from "#app/lib/analytics";

type IssueFilterContextValue = {
  /** The toolbar's session pool — the temporary layer. The chip's panel
   *  edits it and the chip's × clears it; it writes no view. */
  filters: UseFiltersResult;
  /** The VIEW'S filters, bound to the active saved view's config draft — the
   *  layer the Customize view Filters page edits and Save commits. Null
   *  outside a saved-view scope, where there is no view to write. */
  viewFilters: UseFiltersResult | null;
  /** Both layers ANDed — what every query on the screen narrows by. */
  where: Record<string, unknown> | undefined;
  /** Both layers' counts summed — what the toolbar chip wears, since the
   *  chip states how narrowed the screen is, whichever door narrowed it. */
  activeFilterCount: number;
  /** The view layer's count alone — the Customize view Filters row's. */
  viewFilterCount: number;
  /** The view layer's count LESS the fields the toolbar routes to it
   *  (`quickLayerFor`) — what the panel's "saved on this view" note owes,
   *  since a routed field already reads on the panel's own row. */
  viewNoteCount: number;
  /** The layer a quick filter's value lives in. The Assignee filter is
   *  ROUTED: inside a saved view it lives on the view's draft — a pick from
   *  the toolbar chip or the panel's row previews at once, lights Save view
   *  for whoever can save, and is what the view keeps; without perms it is
   *  a temporary edit like any draft row. Outside a view, or for any other
   *  field, it is the session pool. */
  quickLayerFor: QuickFilterLayerFor;
  /** Clears BOTH layers — the chip's × and the ⌥⇧F chord. Clearing the
   *  view layer is a view edit like any other: the draft moves, Save counts
   *  it, Discard puts the filters back. */
  clearAll: (via?: FilterClearVia) => void;
  /** The merged config the panel renders from — static issue fields plus one
   *  `values.<id>` def per workspace custom field. */
  config: FilterConfig;
  filterPanelOpen: boolean;
  /** For overlay `onOpenChange`, which reports a boolean either way. */
  setFilterPanelOpen: (open: boolean) => void;
  openFilterPanel: () => void;
  /** The screen's scope, for the filter analytics of the controls below. */
  analyticsScope: AnalyticsScope;
  closeFilterPanel: () => void;
  toggleFilterPanel: () => void;
};

const IssueFilterContext = createContext<IssueFilterContextValue | null>(null);

/** Quick fields whose value lives on the VIEW layer even when set from the
 *  toolbar — see `quickLayerFor`. The Assignee filter: who a screen is
 *  narrowed to is part of what the view IS (its toolbar chip is a door onto
 *  the view), where every other toolbar filter is a question asked for now. */
const ROUTED_QUICK_FIELDS: ReadonlySet<string> = new Set([ASSIGNEE_PRINCIPAL_FIELD]);

type IssueFilterProviderProps = {
  children: React.ReactNode;
  /** The screen's scope, for `issue.apply_filter` analytics: which screen the
   *  filter narrowed. */
  analyticsScope: AnalyticsScope;
};

/** The control that clears the filters (`issue.clear_filters` `via`): the
 *  chip's ×, the ⌥⇧F chord, or the filter panel's footer. */
export type FilterClearVia = "chip" | "hotkey" | "panel_footer";

/**
 * Shared issue filter state for an issue screen (workflow, team, or member).
 * Scope-agnostic — the scope flows by prop, not through here.
 *
 * TWO layers, and the door decides which one an edit lands in:
 *
 *   filters      the toolbar chip's session pool. Temporary: it starts empty,
 *                writes no view, and never counts toward Save. A question
 *                asked of the screen for now.
 *   viewFilters  the view's own filters, inside a saved-view scope. Seeded
 *                from the view's config draft (`config.filters` /
 *                `config.advancedFilters`), every edit reported back through
 *                `setConfig` — so the view previews it at once, the Save
 *                button counts it, Discard puts it back. A save, a discard or
 *                a remote update bumps `configSeed`, and the layer re-seeds
 *                from the draft that results. The Customize view Filters
 *                page is this layer's only editor.
 *
 * The board, list and dashboard queries read ONE `where` — the two layers
 * ANDed — and the toolbar chip wears the SUMMED count: the chip says how
 * narrowed the screen is, so a filter saved on the view lights it exactly as
 * an ad-hoc one does, and its × clears both (`clearAll`) — clearing a view
 * filter there is a view edit, so Save appears. Key the provider per view so a tab
 * switch starts from that view's own filters and a fresh pool.
 *
 * Outside a saved-view scope (the team and member screens) there is no view
 * to write: `viewFilters` is null and the session pool is the whole set.
 */
export function IssueFilterProvider({ children, analyticsScope }: IssueFilterProviderProps) {
  const config = useIssueFilterConfig();
  const views = useViewsOptional();
  // Bound to the view's draft only once a view is active — the host keys this
  // provider on the view id, so the seed below is a mount-time read.
  const bound = views?.activeView ? views : null;
  const filters = useFilters(config);
  const viewLayer = useFilters(
    config,
    bound
      ? {
          simpleState: bound.config.filters,
          advancedState: bound.config.advancedFilters,
        }
      : undefined,
  );
  const viewFilters = bound ? viewLayer : null;
  const {
    filterState: viewSimpleState,
    advancedState: viewAdvancedState,
    applyState: applyViewState,
  } = viewLayer;

  // Report the view layer's edits to the draft, skipping the mount pass so
  // seeding a view's own filters never marks it dirty. Identity, not content:
  // the re-seed below pre-records the exact objects it applies, so their
  // landing never reports. Both states ride one report: a patch names only
  // what moved, so a Simple edit never re-sends Advanced.
  const setConfig = bound?.setConfig;
  const reportedRef = useRef<{
    simple: FilterState;
    advanced: FilterState;
  } | null>(null);
  useEffect(() => {
    if (!setConfig) return;
    if (reportedRef.current === null) {
      reportedRef.current = {
        simple: viewSimpleState,
        advanced: viewAdvancedState,
      };
      return;
    }
    const patch: Partial<ViewConfig> = {};
    if (reportedRef.current.simple !== viewSimpleState) {
      patch.filters = viewSimpleState;
    }
    if (reportedRef.current.advanced !== viewAdvancedState) {
      patch.advancedFilters = viewAdvancedState;
    }
    if (Object.keys(patch).length === 0) return;
    reportedRef.current = { simple: viewSimpleState, advanced: viewAdvancedState };
    setConfig(patch);
  }, [viewSimpleState, viewAdvancedState, setConfig]);

  // Re-seed when the draft moved for a reason other than this layer's own
  // edits — a save, a discard, a remote update. The seed runs through the
  // stored-shape migration HERE so `applyState`'s own pass returns these same
  // objects, and they are what the report effect sees as already reported.
  // The draft is read through a ref: this keys on the seed alone, never on
  // the config the layer itself writes.
  const boundRef = useRef(bound);
  boundRef.current = bound;
  const configSeed = bound?.configSeed;
  const seenSeedRef = useRef(configSeed);
  // The view layer as it stands, so the re-seed can tell whether it moves.
  const viewStateRef = useRef({ simple: viewSimpleState, advanced: viewAdvancedState });
  viewStateRef.current = { simple: viewSimpleState, advanced: viewAdvancedState };
  // Set when a re-seed replaces the view layer: nobody applied a filter, so
  // the `apply_filter` effect below takes the new set as its baseline.
  const reseedPendingRef = useRef(false);
  useEffect(() => {
    if (seenSeedRef.current === configSeed) return;
    seenSeedRef.current = configSeed;
    const draft = boundRef.current?.config;
    if (!draft) return;
    const seeded = {
      simple: migrateStoredFilterState(draft.filters),
      advanced: migrateStoredFilterState(draft.advancedFilters),
    };
    reportedRef.current = seeded;
    // Only a real replacement re-renders the layer (and so re-runs the
    // effect that consumes the flag); an identical seed must not arm it.
    if (
      seeded.simple !== viewStateRef.current.simple ||
      seeded.advanced !== viewStateRef.current.advanced
    ) {
      reseedPendingRef.current = true;
    }
    applyViewState(seeded.simple, seeded.advanced);
  }, [configSeed, applyViewState]);

  const viewWhere = viewFilters?.where;
  const poolWhere = filters.where;
  const where = useMemo(
    () =>
      combineWhereConditions(
        [viewWhere, poolWhere].filter(
          (c): c is Record<string, unknown> => Boolean(c),
        ),
      ),
    [viewWhere, poolWhere],
  );
  const viewFilterCount = viewFilters?.activeFilterCount ?? 0;
  const activeFilterCount = viewFilterCount + filters.activeFilterCount;

  // The routed fields live on the view while there is one to live on.
  const quickLayerFor = useCallback<QuickFilterLayerFor>(
    (field) =>
      viewFilters && ROUTED_QUICK_FIELDS.has(field) ? viewFilters : filters,
    [viewFilters, filters],
  );
  const routedViewCount = viewFilters
    ? Array.from(ROUTED_QUICK_FIELDS).filter((field) =>
        hasQuickFilterValue(viewFilters.quickValues[field]),
      ).length
    : 0;
  const viewNoteCount = Math.max(0, viewFilterCount - routedViewCount);
  const clearPool = filters.clearAll;
  const clearView = viewFilters?.clearAll;
  const { track } = useAnalytics();
  const clearAll = useCallback((via?: FilterClearVia) => {
    if (activeFilterCount > 0) {
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.ClearFilters,
        data: { count: activeFilterCount, scope: analyticsScope, via },
      });
    }
    clearPool();
    clearView?.();
  }, [activeFilterCount, analyticsScope, clearPool, clearView, track]);

  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  // One `open_filter_panel` per open, whichever control or chord opened it.
  const panelWasOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (filterPanelOpen && !panelWasOpenRef.current) {
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.OpenFilterPanel,
        data: { scope: analyticsScope, count: activeFilterCount },
      });
    }
    panelWasOpenRef.current = filterPanelOpen;
    // Only the open transition counts; the count is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPanelOpen]);

  const openFilterPanel = useCallback(() => setFilterPanelOpen(true), []);
  const closeFilterPanel = useCallback(() => setFilterPanelOpen(false), []);
  const toggleFilterPanel = useCallback(() => setFilterPanelOpen((v) => !v), []);

  // One `apply_filter` event per committed change to the active filter set, for
  // every issue screen (board/list/member) that mounts this provider. We track
  // WHICH categories are active (not just the count) — the dimensions people
  // filter on are the signal a future orchestration layer needs. Keyed on a
  // (count + category-set) signature so panel open/close alone never fires;
  // mount and clear-to-empty are skipped (clearing isn't "applying").
  // Both layers and both editors apply at once, so the active filter set is
  // the union of their blocks — mirroring the combined `where`/count.
  const { filterState: poolSimpleState, advancedState: poolAdvancedState } =
    filters;
  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...poolSimpleState.blocks,
            ...poolAdvancedState.blocks,
            ...(viewFilters ? viewSimpleState.blocks : []),
            ...(viewFilters ? viewAdvancedState.blocks : []),
          ]
            .flatMap((b) => b.filters.map((f) => f.field))
            .filter(Boolean)
            // Custom-field rows carry a per-workspace UUID in the field key —
            // one analytics category, not one per field.
            .map((f) => (isCustomFieldFilterField(f) ? "customField" : f)),
        ),
      ).sort(),
    [
      poolSimpleState,
      poolAdvancedState,
      viewFilters,
      viewSimpleState,
      viewAdvancedState,
    ],
  );
  const signature = `${activeFilterCount}|${categories.join(",")}`;
  const prevSignature = useRef<string | null>(null);
  const poolCount = filters.activeFilterCount;
  const advanced = [
    poolAdvancedState,
    ...(viewFilters ? [viewAdvancedState] : []),
  ].some((state) => state.blocks.some((block) => block.filters.length > 0));
  useEffect(() => {
    if (prevSignature.current === null || reseedPendingRef.current) {
      reseedPendingRef.current = false;
      prevSignature.current = signature;
      return;
    }
    if (prevSignature.current === signature) return;
    prevSignature.current = signature;
    if (activeFilterCount === 0) return;
    track({
      context: AnalyticsContext.Issue,
      event: IssueEvent.ApplyFilter,
      data: {
        count: activeFilterCount,
        categories,
        // Which layer holds the filters: the temporary pool, or the view's own.
        poolCount,
        viewCount: viewFilterCount,
        advanced,
        scope: analyticsScope,
      },
    });
  }, [
    signature,
    activeFilterCount,
    categories,
    poolCount,
    viewFilterCount,
    advanced,
    analyticsScope,
    track,
  ]);

  const value = React.useMemo<IssueFilterContextValue>(
    () => ({
      filters,
      viewFilters,
      where,
      activeFilterCount,
      viewFilterCount,
      viewNoteCount,
      quickLayerFor,
      clearAll,
      config,
      filterPanelOpen,
      setFilterPanelOpen,
      openFilterPanel,
      analyticsScope,
      closeFilterPanel,
      toggleFilterPanel,
    }),
    [
      filters,
      viewFilters,
      where,
      activeFilterCount,
      viewFilterCount,
      viewNoteCount,
      quickLayerFor,
      clearAll,
      config,
      filterPanelOpen,
      setFilterPanelOpen,
      openFilterPanel,
      analyticsScope,
      closeFilterPanel,
      toggleFilterPanel,
    ],
  );

  return (
    <IssueFilterContext.Provider value={value}>
      {children}
    </IssueFilterContext.Provider>
  );
}

export function useIssueFilters(): IssueFilterContextValue {
  const ctx = useContext(IssueFilterContext);
  if (!ctx) {
    throw new Error("useIssueFilters must be used within an IssueFilterProvider");
  }
  return ctx;
}

/**
 * Provider-optional read for screens that render both inside and outside an
 * issue screen's filter scope (the `Dashboard` host: the workflow/team tabs
 * mount a provider, `/my-work` doesn't). Null means no panel on this surface —
 * consumers hide their filter affordances rather than throwing.
 */
export function useIssueFiltersOptional(): IssueFilterContextValue | null {
  return useContext(IssueFilterContext);
}
